import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Permissioned, { actions, resources } from "../permissioned";
import { Link } from "react-router-dom";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import { EasaPageReducer } from "../reducers/autnhive";
import { Reducers, ReleaseNotesPageReducer } from "../reducers";
import no_image from "../assets/images/no_image.png";
import moment from "moment";
import { CustomError } from "../services/interfaces";
import { LocalizationProvider, DatePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import TagsInput from "../components/tagInput";

// UI Icons
import ApproveIcon from "@mui/icons-material/Check";
import RejectIcon from "@mui/icons-material/Block";
import ViewIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";

import ConfirmButton from "../components/confirmButton";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import * as releaseNotesActions from "../actions/releaseNotes";
import { useSnackbar, VariantType } from "notistack";
import axios from "axios";
import {
  createReleaseNote,
  deleteReleaseNote,
  getDownloadURL,
  getSignedURL,
  updateReleaseNote,
  updateReleaseNoteStatus,
} from "../services/releaseNotes";

const LIMIT = 10;
const validRegex = /^[A-Za-z0-9_.\- ]+$/;

const ReleaseTypes = {
  RELEASE_NOTES: "release_notes",
  SALES_NOTES: "sales_enablement_notes",
};

function formatISODateWithoutTz(date: string) {
  const [year, month, day] = date.split("T")[0].split("-");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${day}-${monthNames[parseInt(month) - 1]}-${year}`;
}

function SalesNotesForm({
  values,
  onClose,
  isEdit,
  searchText,
  status,
  setStatus,
  type,
  setType,
}: {
  values: any;
  onClose: () => void;
  isEdit: boolean;
  searchText: string;
  status: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  type: string;
  setType: React.Dispatch<React.SetStateAction<string>>;
}) {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [showSpinner, setShowSpinner] = useState(false);
  const [version, setVersion] = useState("");
  const [tags, setTags] = useState([]);
  const [featureDetails, setFeatureDetails] = useState("");

  const salesfileRef = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressType, setProgressType] = useState("");
  const [salesFileData, setSalesFileData] = useState({
    name: "",
    path: "",
    url: "",
  });
  const [isFileUploading, setIsFileUploading] = useState(false);

  function validateFile(file: File, type: string) {
    if (version === "") {
      enqueueSnackbar(`Please enter name`, {
        variant: "error",
      });
      return false;
    }
    if (file) {
      const fileName = file.name;
      const fileSize = file.size;
      const fileExtension = fileName
        .substring(fileName.lastIndexOf(".") + 1)
        .toLowerCase();

      const allowedExtension = type === "internal" ? ["pdf", "txt"] : ["pdf"];
      if (!["pdf"].includes(fileExtension)) {
        enqueueSnackbar(`Invalid File format`, {
          variant: "warning",
        });
        if (salesfileRef.current) {
          salesfileRef.current.value = "";
        }
        return false;
      }

      return true;
    }
    return false;
  }

  async function uploadSalesFile(file: File) {
    setIsFileUploading(true);
    const fileName = `${version?.replace(/[.\- ]/g, "_")}_Sales_Notes.pdf`;
    // getting singned URL to upload selected file
    const getSignedURLReponse = await getSignedURL({
      version,
      file_name: fileName,
    });

    // rename the file before upload
    const renamedFile = new File([file], fileName, { type: file.type });
    // upload the file to the S3 url
    const uploadFile = await axios.put(getSignedURLReponse.url, renamedFile, {
      headers: {
        "Content-Type": file.type,
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent && progressEvent.total) {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentage);
          setProgressType("Uploading");
        }
      },
    });
    if (uploadFile.status === 200) {
      setProgress(100);
      setProgressType("");
      setSalesFileData({
        name: fileName,
        path: getSignedURLReponse.key,
        url: getSignedURLReponse.url,
      });
      setIsFileUploading(false);
    } else {
      setIsFileUploading(false);
      if (salesfileRef.current) {
        salesfileRef.current.value = "";
      }
      enqueueSnackbar(`File Upload failed.`, {
        variant: "error",
      });
    }
  }

  useEffect(() => {
    if (isEdit && values) {
      setVersion(values?.version);
      setFeatureDetails(values?.feature_details);
      setSalesFileData({
        name: values?.sales_document?.split("/").pop(),
        path: values?.sales_document,
        url: "",
      });
    }
  }, [isEdit]);

  useEffect(() => {
    return () => {
      setVersion("");
      setFeatureDetails("");
      setSalesFileData({
        name: "",
        path: "",
        url: "",
      });
      if (salesfileRef.current) {
        salesfileRef.current.value = "";
      }
    };
  }, []);

  return (
    <Dialog maxWidth={"md"} open onClose={onClose}>
      <DialogTitle sx={{ paddingBottom: "0" }}>
        {isEdit ? "Update Sales Note" : "Create Sales Note"}
      </DialogTitle>
      <DialogContent>
        <Backdrop
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: "#fff",
          }}
          open={showSpinner}
          onClick={() => setShowSpinner(false)}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <form
          style={{
            maxWidth: "600px",
          }}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                placeholder="Enter Name"
                label="Name"
                disabled={isEdit}
                value={version}
                required={true}
                onChange={(e) => {
                  if (isEdit) return;
                  setVersion(e.target.value);
                }}
                onBlur={() => {
                  if (version === "") {
                    enqueueSnackbar("Name cannot be empty", {
                      variant: "error",
                    });
                    return;
                  }
                  if (!validRegex.test(version)) {
                    enqueueSnackbar(
                      "Name can contain only letters, numbers, dot(.), underscores(_) and hyphens(-)",
                      { variant: "error" }
                    );
                  }
                  setVersion(version?.trim());
                }}
                style={{
                  marginTop: "2rem",
                }}
                variant="standard"
              />
            </Grid>
            {/* <Grid item xs={12}>
              <TagsInput
                selectedTags={(items: any) => setTags(items)}
                tags={values?.tags || tags}
                fullWidth
                id="tags"
                name="Tags"
                label="Tags"
                placeholder="Type and press Enter to add tags"
                onDelete={(list: any) => {
                  setTags(list);
                }}
                onChange={(newDomain: any, list: any) => {
                  list.push(newDomain);
                  setTags(list);
                }}
              />
            </Grid> */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Please enter feature details"
                label="Feature Details"
                value={featureDetails}
                onChange={(e) => setFeatureDetails(e.target.value)}
                variant="standard"
                required={true}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={6} position={"relative"}>
              {(salesFileData?.name !== "" && salesFileData?.path !== "") ? <Typography component="p" variant="body1">
                Sales Document
                <br />
                <Typography
                  component="a"
                  variant="body2"
                  sx={{
                    textDecoration: "underline",
                    color: "#0d6efd",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    // setFileName(values?.sales_document?.split("/").pop());
                    // setFilePath(values?.sales_document);
                    // setShowFile(true);
                  }}
                >
                  {values?.sales_document?.split("/").pop()}
                </Typography>
                <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setSalesFileData({
                      name: "",
                      path: "",
                      url: "",
                    })
                  }}
                  style={{
                    padding: "5px",
                    marginLeft: "8px",
                    marginBottom: "1px",
                  }}
                >
                  <EditIcon style={{ width: "18px", height: "18px" }} />
                </IconButton>
              </Typography> : <>
                <TextField
                  fullWidth
                  inputRef={salesfileRef}
                  placeholder="Sales Document"
                  label="Sales Document"
                  required={true}
                  onChange={(e: any) => {
                    const selectedFile = e.target?.files?.[0] || null;
                    if (validateFile(selectedFile, "external")) {
                      uploadSalesFile(selectedFile);
                    }
                  }}
                  variant="standard"
                  type="file"
                  inputProps={{
                    accept: ".pdf",
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {isEdit && values?.sales_document !== "" && <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setSalesFileData({
                      name: values?.sales_document?.split("/").pop(),
                      path: values?.sales_document,
                      url: "",
                    });
                  }}
                  style={{
                    fontSize: "13px",
                    position: "absolute",
                    bottom: "0",
                    right: "-60px",
                    padding: "5px",
                    borderRadius: "8px",
                  }}
                >
                  Cancel
                </IconButton>}
              </>}
              {/* {internalProgressType !== "" && (
                <CircularProgress
                  variant="determinate"
                  value={internalProgress}
                  sx={{ color: "#51b09e", width: "35px", height: "35px" }}
                />
              )} */}
            </Grid>
            {/* submit button */}
            <Grid item xs={12}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  style={{ width: 150 }}
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    let validatedValues = true;
                    if (version === "") {
                      validatedValues = false;
                      enqueueSnackbar(`Please enter name`, {
                        variant: "error",
                      });
                      return validatedValues;
                    }
                    if (version !== "") {
                      if (!validRegex.test(version)) {
                        validatedValues = false;
                        enqueueSnackbar(
                          "Name can contain only letters, numbers, dot(.), underscores(_) and hyphens(-)",
                          { variant: "error" }
                        );
                        return validatedValues;
                      }
                    }
                    // if (tags?.length === 0) {
                    //   validatedValues = false;
                    //   enqueueSnackbar(`Tags cannot be empty`, {
                    //     variant: "error",
                    //   });
                    //   return validatedValues;
                    // }
                    if (featureDetails === "") {
                      validatedValues = false;
                      enqueueSnackbar(`Please enter feature details`, {
                        variant: "error",
                      });
                      return validatedValues;
                    }
                    if (isFileUploading) {
                      validatedValues = false;
                      enqueueSnackbar(`Please wait file upload in progress`, {
                        variant: "info",
                      });
                      return validatedValues;
                    }
                    if (salesFileData?.name === "") {
                      validatedValues = false;
                      enqueueSnackbar(`Please add sales document`, {
                        variant: "error",
                      });
                      return validatedValues;
                    }
                    // after validation, proceed to add
                    if (validatedValues) {
                      const data: any = {
                        type: ReleaseTypes.SALES_NOTES, // default value for sales notes
                        version,
                        sales_document: salesFileData?.path,
                        feature_details: featureDetails,
                      };
                      if (isEdit) {
                        delete data.version;
                        updateReleaseNote({
                          slug: values?.slug,
                          ...data,
                        })
                          .then((res) => {
                            if (res && res?.data) {
                              enqueueSnackbar("Updated Successfully", {
                                variant: "success",
                              });
                              onClose();
                              dispatch(
                                releaseNotesActions.fetchReleaseNotesList({
                                  page: 1,
                                  limit: LIMIT,
                                  searchText,
                                  status,
                                  type,
                                })
                              );
                            }
                          })
                          .catch((err) => {
                            enqueueSnackbar(err?.message, { variant: "error" });
                          });
                      } else {
                        createReleaseNote(data)
                          .then((res) => {
                            if (res && res?.data) {
                              enqueueSnackbar(
                                "Sales Note created Successfully",
                                { variant: "success" }
                              );
                              onClose();
                              dispatch(
                                releaseNotesActions.fetchReleaseNotesList({
                                  page: 1,
                                  limit: LIMIT,
                                })
                              );
                              setStatus("");
                              setType("");
                            }
                          })
                          .catch((err) => {
                            enqueueSnackbar(err?.message, { variant: "error" });
                          });
                      }
                    }
                  }}
                >
                  {isEdit ? "Update" : "Create"}
                </Button>
              </div>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReleaseNotesForm({
  values,
  onClose,
  isEdit,
  searchText,
  status,
  setStatus,
  type,
  setType,
}: {
  values: any;
  onClose: () => void;
  isEdit: boolean;
  searchText: string;
  status: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  type: string;
  setType: React.Dispatch<React.SetStateAction<string>>;
}) {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [showSpinner, setShowSpinner] = useState(false);
  const [version, setVersion] = useState("");
  const [tags, setTags] = useState([]);
  const [featureDetails, setFeatureDetails] = useState("");
  const [releaseDate, setReleaseDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [expectedReleaseDate, setExpectedReleaseDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );

  const [productReleaseNotesPath, setProductReleaseNotesPath] = useState("");
  const [userInterfaceReleaseLogPath, setUserInterfaceReleaseLogPath] =
    useState("");
  const [securityDocumentPath, setSecurityDocumentPath] = useState("");
  const [developerReleaseNotesPath, setDeveloperReleaseNotesPath] =
    useState("");
  const [functionalChangeDocPath, setFunctionalChangeDocPath] = useState("");

  const productReleaseNotesRef = useRef<HTMLInputElement | null>(null);
  const userInterfaceReleaseLogRef = useRef<HTMLInputElement | null>(null);
  const securityDocumentRef = useRef<HTMLInputElement | null>(null);
  const developerReleaseNotesRef = useRef<HTMLInputElement | null>(null);
  const functionalChangeDocRef = useRef<HTMLInputElement | null>(null);

  const [isFileUploading, setIsFileUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressType, setProgressType] = useState("");

  function validateFile(file: File, type: string) {
    if (version === "") {
      enqueueSnackbar(`Please enter Release Version`, {
        variant: "error",
      });
      return false;
    }
    if (file) {
      const fileName = file.name;
      const fileSize = file.size;
      const fileExtension = fileName
        .substring(fileName.lastIndexOf(".") + 1)
        .toLowerCase();

      const allowedExtension = type === "internal" ? ["pdf", "txt"] : ["pdf"];
      if (!allowedExtension.includes(fileExtension)) {
        enqueueSnackbar(`Invalid File format`, {
          variant: "warning",
        });
        return false;
      }

      return true;
    }
    return false;
  }

  async function getFilePath(file: File, fileName: string) {
    setIsFileUploading(true);
    let fileExtension = file.name
      ?.substring(file.name?.lastIndexOf(".") + 1)
      .toLowerCase();
    const fileNameWithExt = `${fileName}.${fileExtension}`;
    // getting singned URL to upload selected file
    const getSignedURLReponse = await getSignedURL({
      version,
      file_name: fileNameWithExt,
    });

    return {
      ...getSignedURLReponse,
      fileName: fileNameWithExt,
    };
  }

  async function uploadFile(file: File, fileName: string, url: string) {
    setIsFileUploading(true);
    // rename the file before upload
    const renamedFile = new File([file], fileName, { type: file.type });
    // upload the file to the S3 url
    const response = await axios.put(url, renamedFile, {
      headers: {
        "Content-Type": file.type,
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent && progressEvent.total) {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentage);
          setProgressType("Uploading");
        }
      },
    });
    if (response.status === 200) {
      setProgress(100);
      setProgressType("");
      setIsFileUploading(false);
    } else {
      setIsFileUploading(false);
      enqueueSnackbar(`File Upload failed.`, {
        variant: "error",
      });
    }
  }

  useEffect(() => {
    if (isEdit && values) {
      setVersion(values?.version);
      setTags(values?.tags);
      setReleaseDate(
        moment(values?.release_date?.split("T")[0]).format("YYYY-MM-DD")
      );
      setExpectedReleaseDate(
        moment(values?.expected_release_date?.split("T")[0]).format(
          "YYYY-MM-DD"
        )
      );
      setFeatureDetails(values?.feature_details);
      setProductReleaseNotesPath(values?.product_release_notes);
      setUserInterfaceReleaseLogPath(values?.user_interface_change_log);
      setSecurityDocumentPath(values?.security_document);
      setDeveloperReleaseNotesPath(values?.developer_release_notes);
      setFunctionalChangeDocPath(values?.functional_change_document);
      // setInternalFileData({
      //   name: values?.internal_document_url?.split("/").pop(),
      //   path: values?.internal_document_url,
      //   url: "",
      // });
    }
  }, [isEdit]);

  useEffect(() => {
    return () => {
      setVersion("");
      setReleaseDate(moment(new Date()).format("YYYY-MM-DD"));
      setExpectedReleaseDate(moment(new Date()).format("YYYY-MM-DD"));
      setTags([]);
      setFeatureDetails("");
      setProductReleaseNotesPath("");
      setUserInterfaceReleaseLogPath("");
      setSecurityDocumentPath("");
      setDeveloperReleaseNotesPath("");
      setFunctionalChangeDocPath("");
      if (productReleaseNotesRef.current) {
        productReleaseNotesRef.current.value = "";
      }
      if (userInterfaceReleaseLogRef.current) {
        userInterfaceReleaseLogRef.current.value = "";
      }
      if (securityDocumentRef.current) {
        securityDocumentRef.current.value = "";
      }
      if (developerReleaseNotesRef.current) {
        developerReleaseNotesRef.current.value = "";
      }
      if (functionalChangeDocRef.current) {
        functionalChangeDocRef.current.value = "";
      }
    };
  }, []);

  return (
    <Dialog maxWidth={"md"} open onClose={onClose}>
      <DialogTitle>
        {isEdit ? "Update Release Note" : "Create Release Note"}
      </DialogTitle>
      <DialogContent>
        <Backdrop
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: "#fff",
          }}
          open={showSpinner}
          onClick={() => setShowSpinner(false)}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <form
          style={{
            maxWidth: "700px",
          }}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                placeholder="Enter Version"
                label="Version"
                disabled={isEdit}
                value={version}
                required={true}
                onChange={(e) => {
                  if (isEdit) return;
                  setVersion(e.target.value);
                }}
                onBlur={() => {
                  if (version === "") {
                    enqueueSnackbar("Version cannot be empty", {
                      variant: "error",
                    });
                    return;
                  }
                  if (!validRegex.test(version)) {
                    enqueueSnackbar(
                      "Version can contain only letters, numbers, dot(.), underscores(_) and hyphens(-)",
                      { variant: "error" }
                    );
                  }
                  setVersion(version?.trim());
                }}
                style={{
                  marginTop: "2rem",
                }}
                variant="standard"
              />
            </Grid>
            {/* <Grid item xs={3}>
              <FormControl
                margin="normal"
                fullWidth
                // required
                style={{
                  marginTop: "2rem",
                }}
                // error={props.error?.validationErrors?.product_id}
              >
                <InputLabel variant="standard" id="select-label">
                  Release Type
                </InputLabel>
                <Select
                  variant="standard"
                  value={releaseType}
                  labelId="select-product"
                  id="select-product"
                  onChange={(e: any) => {
                    setReleaseType(e.target.value as string);
                  }}
                >
                  {["internal", "external", "sales"].map((item) => (
                    <MenuItem key={item} value={item}>
                      {item === "internal"
                        ? "Internal Notes"
                        : item === "external"
                        ? "External Notes"
                        : "Sales Notes"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}
            {/* <Grid item xs={8}>
              <TextField
                fullWidth
                placeholder="Enter JIRA ticket ID"
                label="CR Link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                // onBlur={() => setCustomerName(customerName.trim())}
                variant="standard"
              />
            </Grid> */}
            <Grid item xs={4}>
              <FormControl margin="normal" fullWidth>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    views={["year", "month", "day"]}
                    inputFormat="dd MMM yyyy"
                    mask=""
                    // disabled
                    minDate={new Date()}
                    // maxDate={contract_end_date}
                    key="release-date-id"
                    label="Release Date *"
                    value={releaseDate}
                    onChange={(date: Date | null) => {
                      setReleaseDate(moment(date).format("YYYY-MM-DD"));
                    }}
                    onError={(reason, value) => {
                    }}
                    renderInput={(props) => (
                      <TextField
                        margin="normal"
                        variant="standard"
                        {...props}
                      />
                    )}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl margin="normal" fullWidth>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    views={["year", "month", "day"]}
                    inputFormat="dd MMM yyyy"
                    mask=""
                    // disabled
                    minDate={new Date()}
                    // maxDate={contract_end_date}
                    key="expected-release-date-id"
                    label="Expected Release Date *"
                    value={expectedReleaseDate}
                    onChange={(date: Date | null) => {
                      setExpectedReleaseDate(moment(date).format("YYYY-MM-DD"));
                    }}
                    onError={(reason, value) => { }}
                    renderInput={(props) => (
                      <TextField
                        margin="normal"
                        variant="standard"
                        {...props}
                      />
                    )}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TagsInput
                // disabled={
                //   disabled ||
                //   Boolean(existingCustomer) ||
                //   props.isRenewalContract
                // }
                selectedTags={(items: any) => setTags(items)}
                tags={isEdit ? values?.tags || tags : tags}
                fullWidth
                id="tags"
                name="Tags"
                label="Tags *"
                placeholder="Type and press Enter to add tags"
                onDelete={(list: any) => {
                  setTags(list);
                }}
                onChange={(newTag: any, list: any) => {
                  list.push(newTag);
                  setTags(list);
                }}
                onError={(reason: string, type: VariantType) => {
                  enqueueSnackbar(reason, { variant: type })
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Please enter feature details"
                label="Feature Details"
                value={featureDetails}
                onChange={(e) => setFeatureDetails(e.target.value)}
                variant="standard"
                required={true}
                multiline
                rows={3}
              />
            </Grid>
            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Enter details"
                label="User Information (Optional)"
                value={userInformation}
                onChange={(e) => setUserInformation(e.target.value)}
                // onBlur={() => setCustomerName(customerName.trim())}
                variant="standard"
                multiline
                rows={3}
              />
            </Grid> */}
            <Grid item xs={6} position={"relative"}>
              {(isEdit && productReleaseNotesPath !== "") ? <Typography component="p" variant="body1">
                Product Release Notes
                <br />
                <Typography
                  component="a"
                  variant="body2"
                  sx={{
                    textDecoration: "underline",
                    color: "#0d6efd",
                    cursor: "pointer",
                  }}
                  onClick={() => {

                  }}
                >
                  {values?.product_release_notes?.split("/").pop()?.split("/").pop()}
                </Typography>
                <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setProductReleaseNotesPath("");
                  }}
                  style={{
                    padding: "5px",
                    marginLeft: "8px",
                    marginBottom: "1px",
                  }}
                >
                  <EditIcon style={{ width: "18px", height: "18px" }} />
                </IconButton>
              </Typography> : <>
                <TextField
                  fullWidth
                  inputRef={productReleaseNotesRef}
                  placeholder="Product Release Notes"
                  label="Product Release Notes"
                  onChange={(e: any) => {
                    const selectedFile = e.target?.files?.[0] || null;
                    if (validateFile(selectedFile, "external")) {
                      const fileName = `Product_Release_Notes_v${version?.replace(
                        /[.\- ]/g,
                        "_"
                      )}_External`;
                      getFilePath(selectedFile, fileName)
                        .then((res) => {
                          setProductReleaseNotesPath(res?.key);
                          uploadFile(selectedFile, res?.fileName, res?.url);
                        })
                        .catch((err) => {
                          if (productReleaseNotesRef.current) {
                            productReleaseNotesRef.current.value = "";
                          }
                          enqueueSnackbar(`File Upload failed.`, {
                            variant: "error",
                          });
                        });
                    } else {
                      if (productReleaseNotesRef.current) {
                        productReleaseNotesRef.current.value = "";
                      }
                    }
                  }}
                  variant="standard"
                  type="file"
                  inputProps={{
                    accept: ".pdf",
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {isEdit && values?.product_release_notes !== "" && <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setProductReleaseNotesPath(values?.product_release_notes);
                  }}
                  style={{
                    fontSize: "13px",
                    position: "absolute",
                    bottom: "5px",
                    right: "0",
                    padding: "5px",
                    borderRadius: "8px",
                  }}
                >
                  Cancel
                </IconButton>}
              </>}
              {/* {internalProgressType !== "" && (
            <CircularProgress
              variant="determinate"
              value={internalProgress}
              sx={{ color: "#51b09e", width: "35px", height: "35px" }}
            />
          )} */}
            </Grid>
            <Grid item xs={6} position={"relative"}>
              {(isEdit && userInterfaceReleaseLogPath !== "") ? <Typography component="p" variant="body1">
                User Interface Change Log
                <br />
                <Typography
                  component="a"
                  variant="body2"
                  sx={{
                    textDecoration: "underline",
                    color: "#0d6efd",
                    cursor: "pointer",
                  }}
                  onClick={() => {

                  }}
                >
                  {values?.user_interface_change_log?.split("/").pop()?.split("/").pop()}
                </Typography>
                <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setUserInterfaceReleaseLogPath("");
                  }}
                  style={{
                    padding: "5px",
                    marginLeft: "8px",
                    marginBottom: "1px",
                  }}
                >
                  <EditIcon style={{ width: "18px", height: "18px" }} />
                </IconButton>
              </Typography> : <>
                <TextField
                  fullWidth
                  inputRef={userInterfaceReleaseLogRef}
                  placeholder="User Interface Change Log"
                  label="User Interface Change Log"
                  onChange={(e: any) => {
                    const selectedFile = e.target?.files?.[0] || null;
                    if (validateFile(selectedFile, "external")) {
                      const fileName = `User_Interface_Log_v${version?.replace(
                        /[.\- ]/g,
                        "_"
                      )}_External`;
                      getFilePath(selectedFile, fileName)
                        .then((res) => {
                          setUserInterfaceReleaseLogPath(res?.key);
                          uploadFile(selectedFile, res?.fileName, res?.url);
                        })
                        .catch((err) => {
                          if (userInterfaceReleaseLogRef.current) {
                            userInterfaceReleaseLogRef.current.value = "";
                          }
                          enqueueSnackbar(`File Upload failed.`, {
                            variant: "error",
                          });
                        });
                    } else {
                      if (userInterfaceReleaseLogRef.current) {
                        userInterfaceReleaseLogRef.current.value = "";
                      }
                    }
                  }}
                  variant="standard"
                  type="file"
                  inputProps={{
                    accept: ".pdf",
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {isEdit && values?.user_interface_change_log !== "" && <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setUserInterfaceReleaseLogPath(values?.user_interface_change_log);
                  }}
                  style={{
                    fontSize: "13px",
                    position: "absolute",
                    bottom: "5px",
                    right: "0",
                    padding: "5px",
                    borderRadius: "8px",
                  }}
                >
                  Cancel
                </IconButton>}
              </>}

              {/* {externalProgressType !== "" && (
            <CircularProgress
              variant="determinate"
              value={externalProgress}
              sx={{ color: "#51b09e", width: "35px", height: "35px" }}
            />
          )} */}
            </Grid>
            <Grid item xs={6} position={"relative"}>
              {(isEdit && securityDocumentPath !== "") ? <Typography component="p" variant="body1">
                Security Document
                <br />
                <Typography
                  component="a"
                  variant="body2"
                  sx={{
                    textDecoration: "underline",
                    color: "#0d6efd",
                    cursor: "pointer",
                  }}
                  onClick={() => {

                  }}
                >
                  {values?.security_document?.split("/").pop()?.split("/").pop()}
                </Typography>
                <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setSecurityDocumentPath("");
                  }}
                  style={{
                    padding: "5px",
                    marginLeft: "8px",
                    marginBottom: "1px",
                  }}
                >
                  <EditIcon style={{ width: "18px", height: "18px" }} />
                </IconButton>
              </Typography> : <>
                <TextField
                  fullWidth
                  inputRef={securityDocumentRef}
                  placeholder="Security Document"
                  label="Security Document"
                  onChange={(e: any) => {
                    const selectedFile = e.target?.files?.[0] || null;
                    if (validateFile(selectedFile, "internal")) {
                      const fileName = `Security_Doc_v${version?.replace(
                        /[.\- ]/g,
                        "_"
                      )}_Internal`;
                      getFilePath(selectedFile, fileName)
                        .then((res) => {
                          setSecurityDocumentPath(res?.key);
                          uploadFile(selectedFile, res?.fileName, res?.url);
                        })
                        .catch((err) => {
                          if (securityDocumentRef.current) {
                            securityDocumentRef.current.value = "";
                          }
                          enqueueSnackbar(`File Upload failed.`, {
                            variant: "error",
                          });
                        });
                    } else {
                      if (securityDocumentRef.current) {
                        securityDocumentRef.current.value = "";
                      }
                    }
                  }}
                  variant="standard"
                  type="file"
                  inputProps={{
                    accept: ".pdf, .txt",
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {isEdit && values?.security_document !== "" && <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setSecurityDocumentPath(values?.security_document);
                  }}
                  style={{
                    fontSize: "13px",
                    position: "absolute",
                    bottom: "5px",
                    right: "0",
                    padding: "5px",
                    borderRadius: "8px",
                  }}
                >
                  Cancel
                </IconButton>}
              </>}
            </Grid>
            <Grid item xs={6} position={"relative"}>
              {(isEdit && developerReleaseNotesPath !== "") ? <Typography component="p" variant="body1">
                Developer Release Notes
                <br />
                <Typography
                  component="a"
                  variant="body2"
                  sx={{
                    textDecoration: "underline",
                    color: "#0d6efd",
                    cursor: "pointer",
                  }}
                  onClick={() => {

                  }}
                >
                  {values?.developer_release_notes?.split("/").pop()?.split("/").pop()}
                </Typography>
                <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setDeveloperReleaseNotesPath("");
                  }}
                  style={{
                    padding: "5px",
                    marginLeft: "8px",
                    marginBottom: "1px",
                  }}
                >
                  <EditIcon style={{ width: "18px", height: "18px" }} />
                </IconButton>
              </Typography> : <>
                <TextField
                  fullWidth
                  inputRef={developerReleaseNotesRef}
                  placeholder="Developer Release Notes"
                  label="Developer Release Notes"
                  onChange={(e: any) => {
                    const selectedFile = e.target?.files?.[0] || null;
                    if (validateFile(selectedFile, "internal")) {
                      const fileName = `Developer_Release_Notes_v${version?.replace(
                        /[.\- ]/g,
                        "_"
                      )}_Internal`;
                      getFilePath(selectedFile, fileName)
                        .then((res) => {
                          setDeveloperReleaseNotesPath(res?.key);
                          uploadFile(selectedFile, res?.fileName, res?.url);
                        })
                        .catch((err) => {
                          if (developerReleaseNotesRef.current) {
                            developerReleaseNotesRef.current.value = "";
                          }
                          enqueueSnackbar(`File Upload failed.`, {
                            variant: "error",
                          });
                        });
                    } else {
                      if (developerReleaseNotesRef.current) {
                        developerReleaseNotesRef.current.value = "";
                      }
                    }
                  }}
                  variant="standard"
                  type="file"
                  inputProps={{
                    accept: ".pdf, .txt",
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {isEdit && values?.developer_release_notes !== "" && <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setDeveloperReleaseNotesPath(values?.developer_release_notes);
                  }}
                  style={{
                    fontSize: "13px",
                    position: "absolute",
                    bottom: "5px",
                    right: "0",
                    padding: "5px",
                    borderRadius: "8px",
                  }}
                >
                  Cancel
                </IconButton>}
              </>}
            </Grid>
            <Grid item xs={6} position={"relative"}>
              {(isEdit && functionalChangeDocPath !== "") ? <Typography component="p" variant="body1">
                Developer Release Notes
                <br />
                <Typography
                  component="a"
                  variant="body2"
                  sx={{
                    textDecoration: "underline",
                    color: "#0d6efd",
                    cursor: "pointer",
                  }}
                  onClick={() => {

                  }}
                >
                  {values?.functional_change_document?.split("/").pop()?.split("/").pop()}
                </Typography>
                <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setFunctionalChangeDocPath("");
                  }}
                  style={{
                    padding: "5px",
                    marginLeft: "8px",
                    marginBottom: "1px",
                  }}
                >
                  <EditIcon style={{ width: "18px", height: "18px" }} />
                </IconButton>
              </Typography> : <>
                <TextField
                  fullWidth
                  inputRef={functionalChangeDocRef}
                  placeholder="Functional Change Documentation"
                  label="Functional Change Documentation"
                  onChange={(e: any) => {
                    const selectedFile = e.target?.files?.[0] || null;
                    if (validateFile(selectedFile, "internal")) {
                      const fileName = `Functional_Change_Doc_v${version?.replace(
                        /[.\- ]/g,
                        "_"
                      )}_Internal`;
                      getFilePath(selectedFile, fileName)
                        .then((res) => {
                          setFunctionalChangeDocPath(res?.key);
                          uploadFile(selectedFile, res?.fileName, res?.url);
                        })
                        .catch((err) => {
                          if (functionalChangeDocRef.current) {
                            functionalChangeDocRef.current.value = "";
                          }
                          enqueueSnackbar(`File Upload failed.`, {
                            variant: "error",
                          });
                        });
                    } else {
                      if (functionalChangeDocRef.current) {
                        functionalChangeDocRef.current.value = "";
                      }
                    }
                  }}
                  variant="standard"
                  type="file"
                  inputProps={{
                    accept: ".pdf, .txt",
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {isEdit && values?.functional_change_document !== "" && <IconButton
                  aria-label="Edit File"
                  size="medium"
                  onClick={() => {
                    setFunctionalChangeDocPath(values?.functional_change_document);
                  }}
                  style={{
                    fontSize: "13px",
                    position: "absolute",
                    bottom: "5px",
                    right: "0",
                    padding: "5px",
                    borderRadius: "8px",
                  }}
                >
                  Cancel
                </IconButton>}
              </>}
            </Grid>

            {/* submit button */}
            <Grid item xs={12}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  style={{ width: 150 }}
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    let validatedValues = true;
                    if (version === "") {
                      validatedValues = false;
                      enqueueSnackbar(`Please enter Release Version`, {
                        variant: "error",
                      });
                      return validatedValues;
                    }
                    if (version !== "") {
                      if (!validRegex.test(version)) {
                        validatedValues = false;
                        enqueueSnackbar(
                          "Version can contain only letters, numbers, dot(.), underscores(_) and hyphens(-)",
                          { variant: "error" }
                        );
                        return validatedValues;
                      }
                    }
                    if (tags?.length === 0) {
                      validatedValues = false;
                      enqueueSnackbar(`Tags cannot be empty`, {
                        variant: "error",
                      });
                      return validatedValues;
                    }
                    // if (featureDetails === "") {
                    //   validatedValues = false;
                    //   enqueueSnackbar(`Please enter feature details`, {
                    //     variant: "error",
                    //   });
                    //   return validatedValues;
                    // }
                    if (isFileUploading) {
                      validatedValues = false;
                      enqueueSnackbar(`Please wait file upload in progress`, {
                        variant: "info",
                      });
                      return validatedValues;
                    }
                    // after validation, proceed to add
                    if (validatedValues) {
                      const data: any = {
                        type: ReleaseTypes.RELEASE_NOTES, // default value
                        version,
                        tags,
                        release_date: releaseDate,
                        expected_release_date: expectedReleaseDate,
                        feature_details: featureDetails,
                        product_release_notes: productReleaseNotesPath,
                        user_interface_change_log: userInterfaceReleaseLogPath,
                        security_document: securityDocumentPath,
                        developer_release_notes: developerReleaseNotesPath,
                        functional_change_document: functionalChangeDocPath,
                      };
                      if (isEdit) {
                        delete data.version;
                        updateReleaseNote({
                          slug: values?.slug,
                          ...data,
                        })
                          .then((res) => {
                            if (res && res?.data) {
                              enqueueSnackbar("Updated Successfully", {
                                variant: "success",
                              });
                              onClose();
                              dispatch(
                                releaseNotesActions.fetchReleaseNotesList({
                                  page: 1,
                                  limit: LIMIT,
                                  searchText,
                                  status,
                                  type,
                                })
                              );
                            }
                          })
                          .catch((err) => {
                            enqueueSnackbar(err?.message, { variant: "error" });
                          });
                      } else {
                        createReleaseNote(data)
                          .then((res) => {
                            if (res && res?.data) {
                              enqueueSnackbar(
                                "Release Note created Successfully",
                                { variant: "success" }
                              );
                              onClose();
                              dispatch(
                                releaseNotesActions.fetchReleaseNotesList({
                                  page: 1,
                                  limit: LIMIT,
                                })
                              );
                              setStatus("");
                              setType("");
                            }
                          })
                          .catch((err) => {
                            enqueueSnackbar(err?.message, { variant: "error" });
                          });
                      }
                    }
                  }}
                >
                  {isEdit ? "Update" : "Create"}
                </Button>
              </div>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ViewFile({
  fileName,
  filePath,
  onClose,
}: {
  fileName: string;
  filePath: string;
  onClose: () => void;
}) {
  const [fileUrl, setFileUrl] = useState("");
  const [showSpinner, setShowSpinner] = useState(true);

  useEffect(() => {
    getDownloadURL({ file_path: filePath })
      .then((res) => {
        if (res?.url !== "") {
          setFileUrl(res?.url);
          setShowSpinner(false);
        }
      })
      .catch((err) => {
        setShowSpinner(false);
        // enqueueSnackbar("Cannot download the file", { variant: "error" });
      });
  }, [filePath]);

  return (
    <Dialog
      maxWidth={"lg"}
      open
      onClose={onClose}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          width: "900px",
          height: "80vh",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: "0" }}>
        <span
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {fileName}
          <IconButton
            aria-label="Download file"
            size="large"
            // disabled
            onClick={async () => {
              // const link = document.createElement("a");
              // link.href = fileUrl;
              // link.download = "";
              // document.body.appendChild(link);
              // link.click();
              // document.body.removeChild(link);

              const res = await fetch(fileUrl);
              const blob = await res.blob();

              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = fileName;
              a.click();

              window.URL.revokeObjectURL(url);
            }}
          >
            <DownloadIcon />
          </IconButton>
        </span>
      </DialogTitle>
      <DialogContent
        sx={{
          marginTop: "1rem",
          overflow: "hidden",
        }}
      >
        {/* <Backdrop
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: "#fff",
          }}
          open={showSpinner}
          onClick={() => setShowSpinner(false)}
        >
        </Backdrop> */}
        {showSpinner && (
          <span
            className="w-100"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress color="inherit" />
          </span>
        )}
        {fileUrl !== "" ? (
          <iframe
            src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            loading="lazy"
            width={"100%"}
            height={"100%"}
            style={{ border: "none" }}
            referrerPolicy="no-referrer"
            title={fileName}
          />
        ) : (
          <></>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ViewNotes({ values, onClose }: { values: any; onClose: () => void }) {
  const [showFile, setShowFile] = useState(false);
  const [fileName, setFileName] = useState<string>("");
  const [filePath, setFilePath] = useState<string>("");

  return (
    <Dialog maxWidth={"md"} open onClose={onClose} fullWidth>
      <DialogTitle sx={{ paddingBottom: "0" }}>
        {values?.version}
        {values?.type === ReleaseTypes.RELEASE_NOTES
          ? " - Release Notes"
          : " - Sales Notes"}
      </DialogTitle>
      <DialogContent sx={{ marginTop: "1rem" }}>
        <Grid container spacing={3}>
          {values?.type === ReleaseTypes.RELEASE_NOTES && (
            <>
              <Grid item xs={4}>
                <Typography component="p" variant="body1">
                  Released Date
                  <br />
                  <Typography component="span" variant="body2">
                    {values?.release_date}
                  </Typography>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography component="p" variant="body1">
                  Expected Released Date
                  <br />
                  <Typography component="span" variant="body2">
                    {values?.expected_release_date}
                  </Typography>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography component="p" variant="body1">
                  Tags
                  <br />
                  <Typography component="span" variant="body2">
                    {values?.tags?.join(", ")}
                  </Typography>
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography component="p" variant="body1">
              Feature Details
              <br />
              <Typography
                component="span"
                variant="body2"
                dangerouslySetInnerHTML={{ __html: values?.feature_details }}
              ></Typography>
            </Typography>
          </Grid>
          {values?.type === ReleaseTypes.SALES_NOTES ? (
            <>
              <Grid item xs={12}>
                <Typography component="p" variant="body1">
                  Sales Document
                  <br />
                  <Typography
                    component="a"
                    variant="body2"
                    sx={{
                      textDecoration: "underline",
                      color: "#0d6efd",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setFileName(values?.sales_document?.split("/").pop());
                      setFilePath(values?.sales_document);
                      setShowFile(true);
                    }}
                  >
                    {values?.sales_document?.split("/").pop()}
                  </Typography>
                </Typography>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography component="p" variant="h6">
                  External Notes
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography component="p" variant="body1">
                  Product Release Notes
                  <br />
                  {values?.product_release_notes === "" ? (
                    <Typography component="p" variant="body2">
                      No file
                    </Typography>
                  ) : (
                    <Typography
                      component="a"
                      variant="body2"
                      sx={{
                        textDecoration: "underline",
                        color: "#0d6efd",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setFileName(
                          values?.product_release_notes?.split("/").pop()
                        );
                        setFilePath(values?.product_release_notes);
                        setShowFile(true);
                      }}
                    >
                      {values?.product_release_notes?.split("/").pop()}
                    </Typography>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography component="p" variant="body1">
                  User Interface Change Log
                  <br />
                  {values?.user_interface_change_log === "" ? (
                    <Typography component="p" variant="body2">
                      No file
                    </Typography>
                  ) : (
                    <Typography
                      component="a"
                      variant="body2"
                      sx={{
                        textDecoration: "underline",
                        color: "#0d6efd",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setFileName(
                          values?.user_interface_change_log?.split("/").pop()
                        );
                        setFilePath(values?.user_interface_change_log);
                        setShowFile(true);
                      }}
                    >
                      {values?.user_interface_change_log?.split("/").pop()}
                    </Typography>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography component="p" variant="h6">
                  Internal Notes
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography component="p" variant="body1">
                  Security Document
                  <br />
                  {values?.security_document === "" ? (
                    <Typography component="p" variant="body2">
                      No file
                    </Typography>
                  ) : (
                    <Typography
                      component="a"
                      variant="body2"
                      sx={{
                        textDecoration: "underline",
                        color: "#0d6efd",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setFileName(
                          values?.security_document?.split("/").pop()
                        );
                        setFilePath(values?.security_document);
                        setShowFile(true);
                      }}
                    >
                      {values?.security_document?.split("/").pop()}
                    </Typography>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography component="p" variant="body1">
                  Developer Release Notes
                  <br />
                  {values?.developer_release_notes === "" ? (
                    <Typography component="p" variant="body2">
                      No file
                    </Typography>
                  ) : (
                    <Typography
                      component="a"
                      variant="body2"
                      sx={{
                        textDecoration: "underline",
                        color: "#0d6efd",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setFileName(
                          values?.developer_release_notes?.split("/").pop()
                        );
                        setFilePath(values?.developer_release_notes);
                        setShowFile(true);
                      }}
                    >
                      {values?.developer_release_notes?.split("/").pop()}
                    </Typography>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography component="p" variant="body1">
                  Functional Change Documentation
                  <br />
                  {values?.functional_change_document === "" ? (
                    <Typography component="p" variant="body2">
                      No file
                    </Typography>
                  ) : (
                    <Typography
                      component="a"
                      variant="body2"
                      sx={{
                        textDecoration: "underline",
                        color: "#0d6efd",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setFileName(
                          values?.functional_change_document?.split("/").pop()
                        );
                        setFilePath(values?.functional_change_document);
                        setShowFile(true);
                      }}
                    >
                      {values?.functional_change_document?.split("/").pop()}
                    </Typography>
                  )}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
        {showFile && (
          <ViewFile
            fileName={fileName}
            filePath={filePath}
            onClose={() => setShowFile(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ReleaseNotes() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const disabledMessage =
    "You can't perform this action. Unpublish the release note to continue";
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);

  const [showViewPopup, setShowViewPopup] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showSalesNotes, setShowSalesNotes] = useState(false);
  const [showFile, setShowFile] = useState(false);

  const [searchText, setSearchText] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [type, setType] = useState<string>("");

  const [values, setValues] = useState<any>(null);
  const [filePath, setFilePath] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  const releaseNotesPageReducer: ReleaseNotesPageReducer = useSelector<
    Reducers,
    ReleaseNotesPageReducer
  >((state) => state.releaseNotes);

  async function downloadFile(filePath: string) {
    getDownloadURL({ file_path: filePath })
      .then((res) => {
        if (res?.url !== "") {
          window.open(res?.url, "_blank");
        }
      })
      .catch((err) => {
        enqueueSnackbar("Cannot download the file", { variant: "error" });
      });
    // const response = await fetch(downloadUrl, { method: "GET" });
    // const blob = await response.blob();
    // const file = new File([blob], fileName, { type: "application/pdf" });
    // const fileUrl = URL.createObjectURL(file);
  }

  useEffect(() => {
    dispatch(
      releaseNotesActions.fetchReleaseNotesList({
        page: 1,
        limit: LIMIT,
      })
    );
    return () => {
      dispatch(releaseNotesActions.resetStates());
    };
  }, [dispatch]);

  return (
    <div>
      <Typography component="h1" variant="h5">
        Release Notes
      </Typography>

      <div className="mb-2">
        <Grid container spacing={3}>
          <Grid item>
            <TextField
              size="small"
              label="Search by Version / Tag"
              variant="outlined"
              placeholder="Press Enter to search"
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              onKeyPress={(e) => {
                if (e.key !== "Enter") {
                  return;
                }
                dispatch(
                  releaseNotesActions.fetchReleaseNotesList({
                    page: 1,
                    limit: LIMIT,
                    searchText,
                    status,
                    type,
                  })
                );
              }}
            />
          </Grid>
          <Grid item>
            <FormControl fullWidth sx={{ marginTop: "-12px" }}>
              <InputLabel id="status">Status</InputLabel>
              <Select
                fullWidth
                id="status"
                variant="standard"
                style={{ minWidth: "100px" }}
                value={status}
                onChange={(e: any) => {
                  let value = e.target.value as "active" | "in-active" | "";
                  setStatus(value);
                  dispatch(
                    releaseNotesActions.fetchReleaseNotesList({
                      page: 1,
                      limit: LIMIT,
                      searchText,
                      status: value,
                      type,
                    })
                  );
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="in-active">In-Active</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl fullWidth sx={{ marginTop: "-12px" }}>
              <InputLabel id="status">Type</InputLabel>
              <Select
                fullWidth
                id="status"
                variant="standard"
                style={{ minWidth: "100px" }}
                value={type}
                onChange={(e: any) => {
                  let value = e.target.value as
                    | "release_notes"
                    | "sales_enablement_notes"
                    | "";
                  setType(value);
                  dispatch(
                    releaseNotesActions.fetchReleaseNotesList({
                      page: 1,
                      limit: LIMIT,
                      searchText,
                      status,
                      type: value,
                    })
                  );
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="release_notes">Release Notes</MenuItem>
                <MenuItem value="sales_enablement_notes">Sales Notes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Permissioned
              resource={resources.ReleaseNotes}
              action={actions.Write}
            >
              <Button
                color="secondary"
                variant="outlined"
                // startIcon={<NewReleasesIcon />}
                onClick={() => {
                  setIsEdit(false);
                  setShowNotes(true);
                }}
              >
                New Release Note
              </Button>
            </Permissioned>
            {/* </Link> */}
          </Grid>
          <Grid item>
            <Permissioned
              resource={resources.SalesNotes}
              action={actions.Write}
            >
              <Button
                color="secondary"
                variant="outlined"
                // startIcon={<NewReleasesIcon />}
                onClick={() => {
                  setIsEdit(false);
                  setShowSalesNotes(true);
                }}
              >
                New Sales Note
              </Button>
            </Permissioned>
          </Grid>
        </Grid>
        <div
          className="app-container domanis-table"
          style={{
            display: "flex",
            padding: "1rem",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <table className="domains-table">
            <thead>
              <tr>
                <th>Notes Type</th>
                <th>Name / Version</th>
                <th>Release Date</th>
                <th>Expected Release Date</th>
                <th>Created By</th>
                <th>Download Notes</th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            {releaseNotesPageReducer?.loading ? (
              <tbody>
                <tr>
                  <td colSpan={9}>
                    <CircularProgress size={26} color="inherit" />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {releaseNotesPageReducer?.releasNotesList.map((item, index) => (
                  <tr
                    key={index}
                    style={{ cursor: "pointer", fontSize: "14px" }}
                  >
                    <td>{item?.type === ReleaseTypes.RELEASE_NOTES ? 'Release Notes' : 'Sales Notes'}</td>
                    <td>{item?.version}</td>
                    {/* <ImageNotSupportedIcon */}
                    <td>
                      {item?.type === ReleaseTypes.RELEASE_NOTES
                        ? formatISODateWithoutTz(item?.release_date)
                        : "-"}
                    </td>
                    <td>
                      {item?.type === ReleaseTypes.RELEASE_NOTES
                        ? formatISODateWithoutTz(item?.expected_release_date)
                        : "-"}
                    </td>
                    <td>{item?.created_by?.name}</td>
                    <td>
                      {item?.product_release_notes || item?.sales_document ? (
                        <span
                          style={{
                            textDecoration: "underline",
                            color: "rgb(13, 110, 253)",
                          }}
                          role="button"
                          onClick={() => {
                            if (item?.type === ReleaseTypes.RELEASE_NOTES) {
                              if (
                                item?.product_release_notes &&
                                item?.product_release_notes !== ""
                              ) {
                                // setFileName(
                                //   `Product_Release_Notes_v${item?.version?.replace(
                                //     /[.\- ]/g,
                                //     "_"
                                //   )}_External`
                                // );
                                setFileName(
                                  item?.product_release_notes.split("/").pop()
                                );
                                setFilePath(item?.product_release_notes);
                                setShowFile(true);
                              }
                            } else {
                              if (
                                item?.sales_document &&
                                item?.sales_document !== ""
                              ) {
                                // setFileName(
                                //   `${item?.version?.replace(
                                //     /[.\- ]/g,
                                //     "_"
                                //   )}_Sales_Notes`
                                // );
                                setFileName(
                                  item?.sales_document.split("/").pop()
                                );
                                setFilePath(item?.sales_document);
                                setShowFile(true);
                              }
                            }
                          }}
                        >
                          {item?.type === ReleaseTypes.RELEASE_NOTES
                            ? `SAMI_Release_Notes_v${item?.version?.replace(
                              /[.\- ]/g,
                              "_"
                            )}.pdf`
                            : `SAMI_Sales_Notes_${formatISODateWithoutTz(item?.created_at || '')?.replaceAll("-", "_")}.pdf`
                          }
                        </span>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                    {/* <td>
                      <span
                        style={{
                          textDecoration: "underline",
                          color: "rgb(13, 110, 253)",
                        }}
                        role="button"
                        onClick={() => {
                          if (item?.external_document_url !== "") {
                            downloadFile(item?.external_document_url);
                          }
                        }}
                      >
                        {`Release_v${item?.version?.replace(
                          /[.\- ]/g,
                          "_"
                        )}_External`}
                      </span>
                    </td> */}
                    {item?.type === ReleaseTypes.RELEASE_NOTES ? <td>
                      <Permissioned
                        resource={resources.ReleaseNotes}
                        action={actions.Publish}
                      >
                        {item?.is_active ? (
                          <ConfirmButton
                            heading="Confirm"
                            message={`Are you sure you want to unpublish this ${item?.type === ReleaseTypes.RELEASE_NOTES
                              ? "Release"
                              : "Sales"
                              } note? It will no longer be visible to users.`}
                            component={IconButton}
                            onClick={() => {
                              updateReleaseNoteStatus({
                                slug: item?.slug,
                                // is_active: false,
                              })
                                .then((res) => {
                                  enqueueSnackbar(
                                    item?.type === ReleaseTypes.RELEASE_NOTES
                                      ? `Release Note unpublished.`
                                      : "Sales Note unpublished",
                                    {
                                      variant: "success",
                                    }
                                  );
                                  dispatch(
                                    releaseNotesActions.fetchReleaseNotesList({
                                      page: 1,
                                      limit: LIMIT,
                                      searchText,
                                      status,
                                      type,
                                    })
                                  );
                                })
                                .catch((err) => {
                                  enqueueSnackbar(err.message, {
                                    variant: "error",
                                  });
                                });
                            }}
                          >
                            <Tooltip
                              title={
                                item?.type === ReleaseTypes.RELEASE_NOTES
                                  ? "Unpublish Release Note"
                                  : "Unpublish Sales Note"
                              }
                            >
                              <RejectIcon />
                            </Tooltip>
                          </ConfirmButton>
                        ) : (
                          <ConfirmButton
                            heading="Confirm"
                            message={`Do you want to publish this ${item?.type === ReleaseTypes.RELEASE_NOTES
                              ? "Release"
                              : "Sales"
                              } Note? It will become visible to subscribed users`}
                            component={IconButton}
                            onClick={() => {
                              updateReleaseNoteStatus({
                                slug: item?.slug,
                                // is_active: true,
                              })
                                .then((res) => {
                                  enqueueSnackbar(
                                    `${item?.type === ReleaseTypes.RELEASE_NOTES
                                      ? "Release"
                                      : "Sales"
                                    } note published successfully.`,
                                    {
                                      variant: "success",
                                    }
                                  );
                                  dispatch(
                                    releaseNotesActions.fetchReleaseNotesList({
                                      page: 1,
                                      limit: LIMIT,
                                      searchText,
                                      status,
                                      type,
                                    })
                                  );
                                })
                                .catch((err) => {
                                  enqueueSnackbar(err.message, {
                                    variant: "error",
                                  });
                                });
                            }}
                          >
                            <Tooltip
                              title={
                                item?.type === ReleaseTypes.RELEASE_NOTES
                                  ? "Publish Release Note"
                                  : "Publish Sales Note"
                              }
                            >
                              <ApproveIcon />
                            </Tooltip>
                          </ConfirmButton>
                        )}
                      </Permissioned>
                    </td> : <td>
                      <Permissioned
                        resource={resources.SalesNotes}
                        action={actions.Publish}
                      >
                        {item?.is_active ? (
                          <ConfirmButton
                            heading="Confirm"
                            message={`Are you sure you want to unpublish this ${item?.type === ReleaseTypes.RELEASE_NOTES
                              ? "Release"
                              : "Sales"
                              } note? It will no longer be visible to users.`}
                            component={IconButton}
                            onClick={() => {
                              updateReleaseNoteStatus({
                                slug: item?.slug,
                                // is_active: false,
                              })
                                .then((res) => {
                                  enqueueSnackbar(
                                    item?.type === ReleaseTypes.RELEASE_NOTES
                                      ? `Release Note unpublished.`
                                      : "Sales Note unpublished",
                                    {
                                      variant: "success",
                                    }
                                  );
                                  dispatch(
                                    releaseNotesActions.fetchReleaseNotesList({
                                      page: 1,
                                      limit: LIMIT,
                                      searchText,
                                      status,
                                      type,
                                    })
                                  );
                                })
                                .catch((err) => {
                                  enqueueSnackbar(err.message, {
                                    variant: "error",
                                  });
                                });
                            }}
                          >
                            <Tooltip
                              title={
                                item?.type === ReleaseTypes.RELEASE_NOTES
                                  ? "Unpublish Release Note"
                                  : "Unpublish Sales Note"
                              }
                            >
                              <RejectIcon />
                            </Tooltip>
                          </ConfirmButton>
                        ) : (
                          <ConfirmButton
                            heading="Confirm"
                            message={`Do you want to publish this ${item?.type === ReleaseTypes.RELEASE_NOTES
                              ? "Release"
                              : "Sales"
                              } Note? It will become visible to subscribed users`}
                            component={IconButton}
                            onClick={() => {
                              updateReleaseNoteStatus({
                                slug: item?.slug,
                                // is_active: true,
                              })
                                .then((res) => {
                                  enqueueSnackbar(
                                    `${item?.type === ReleaseTypes.RELEASE_NOTES
                                      ? "Release"
                                      : "Sales"
                                    } note published successfully.`,
                                    {
                                      variant: "success",
                                    }
                                  );
                                  dispatch(
                                    releaseNotesActions.fetchReleaseNotesList({
                                      page: 1,
                                      limit: LIMIT,
                                      searchText,
                                      status,
                                      type,
                                    })
                                  );
                                })
                                .catch((err) => {
                                  enqueueSnackbar(err.message, {
                                    variant: "error",
                                  });
                                });
                            }}
                          >
                            <Tooltip
                              title={
                                item?.type === ReleaseTypes.RELEASE_NOTES
                                  ? "Publish Release Note"
                                  : "Publish Sales Note"
                              }
                            >
                              <ApproveIcon />
                            </Tooltip>
                          </ConfirmButton>
                        )}
                      </Permissioned>
                    </td>}
                    <td>
                      {item?.is_active ? (
                        <span>
                          <IconButton
                            aria-label="View contract"
                            size="large"
                            onClick={() => {
                              setValues(item);
                              setShowViewPopup(true);
                              // setIsEdit(true);
                              // if (item?.type === ReleaseTypes.RELEASE_NOTES) {
                              // } else {
                              //   setShowSalesNotes(true);
                              // }
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </span>
                      ) : (
                        <Tooltip
                          title={
                            item?.is_active
                              ? disabledMessage
                              : item?.type === ReleaseTypes.RELEASE_NOTES
                                ? "Edit Release Note"
                                : "Edit Sales Note"
                          }
                        >
                          <span>
                            <IconButton
                              aria-label="Edit contract"
                              size="large"
                              disabled={item?.is_active}
                              onClick={() => {
                                setValues(item);
                                setIsEdit(true);
                                if (item?.type === ReleaseTypes.RELEASE_NOTES) {
                                  setShowNotes(true);
                                } else {
                                  setShowSalesNotes(true);
                                }
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </td>
                    <td>
                      <Tooltip
                        title={
                          item?.is_active
                            ? disabledMessage
                            : item?.type === ReleaseTypes.RELEASE_NOTES
                              ? "Delete Release Note"
                              : "Delete Sales Note"
                        }
                      >
                        <span>
                          <ConfirmButton
                            heading="Confirm"
                            disabled={item?.is_active}
                            message={`Do you want to delete this ${item?.type === ReleaseTypes.RELEASE_NOTES
                              ? "Release"
                              : "Sales"
                              } Note? It will completely remove all files related to this release.`}
                            component={IconButton}
                            onClick={() => {
                              deleteReleaseNote({
                                slug: item?.slug,
                              })
                                .then((res) => {
                                  enqueueSnackbar(
                                    `${item?.type === ReleaseTypes.RELEASE_NOTES
                                      ? "Release"
                                      : "Sales"
                                    } note deleted successfully.`,
                                    {
                                      variant: "success",
                                    }
                                  );
                                  dispatch(
                                    releaseNotesActions.fetchReleaseNotesList({
                                      page: 1,
                                      limit: LIMIT,
                                      searchText,
                                      status,
                                      type,
                                    })
                                  );
                                })
                                .catch((err) => {
                                  enqueueSnackbar(err.message, {
                                    variant: "error",
                                  });
                                });
                            }}
                          >
                            <DeleteIcon />
                          </ConfirmButton>
                        </span>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
                {!releaseNotesPageReducer?.releasNotesList ||
                  releaseNotesPageReducer?.releasNotesList.length === 0 ||
                  releaseNotesPageReducer.count === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        fontStyle: "italic",
                        fontWeight: "normal",
                        color: "#343434",
                        padding: "0",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <span className="py-3">No data available</span>
                    </td>
                  </tr>
                ) : (
                  ""
                )}
              </tbody>
            )}
          </table>
        </div>
        {releaseNotesPageReducer?.count > LIMIT && (
          <Pagination
            count={Math.ceil(releaseNotesPageReducer?.count / LIMIT)}
            variant="outlined"
            shape="rounded"
            onChange={(e: React.ChangeEvent<unknown>, newPage: number) => {
              setPage(newPage);
              dispatch(
                releaseNotesActions.fetchReleaseNotesList({
                  page: newPage,
                  limit: LIMIT,
                  searchText,
                  status,
                  type,
                })
              );
            }}
          />
        )}
      </div>
      {/* create release notes */}
      {showNotes && (
        <ReleaseNotesForm
          values={values}
          onClose={() => setShowNotes(false)}
          isEdit={isEdit}
          searchText={searchText}
          status={status}
          setStatus={setStatus}
          type={type}
          setType={setType}
        />
      )}
      {showSalesNotes && (
        <SalesNotesForm
          values={values}
          onClose={() => setShowSalesNotes(false)}
          isEdit={isEdit}
          searchText={searchText}
          status={status}
          setStatus={setStatus}
          type={type}
          setType={setType}
        />
      )}
      {showFile && (
        <ViewFile
          fileName={fileName}
          filePath={filePath}
          onClose={() => setShowFile(false)}
        />
      )}
      {showViewPopup && (
        <ViewNotes values={values} onClose={() => setShowViewPopup(false)} />
      )}
    </div>
  );
}
